import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaBullhorn, FaCalendarAlt, FaLightbulb, FaBoxOpen, FaCheckCircle } from 'react-icons/fa';
import ConfirmDialog from '../common/ConfirmDialog';
import ShareModal from '../scheduleCampaign/ShareModal';
import { deleteCampaignRequest } from '../../redux/slices/campaignsSlice';
import { deleteScheduleRequest } from '../../redux/slices/campaignScheduleSlice';
import { fetchIntegrationsRequest } from '../../redux/slices/integrationsSlice';
import { fetchRestaurantRequest } from '../../redux/slices/restaurantSlice';
import { fetchUpcomingEventsRequest } from '../../redux/slices/upcomingEventsSlice';
import type { RootState } from '../../redux/store';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';
import { useCampaigns } from '../../hooks/useCampaigns';
import { DashboardHeader } from './DashboardHeader';
import { DashboardEmptyState } from './DashboardEmptyState';
import { CampaignScrollSection } from './CampaignScrollSection';

const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const { navigate } = useRestaurantNav();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<'campaign' | 'schedule'>('campaign');
  const [showShareModal, setShowShareModal] = useState(false);
  const [campaignToShare, setCampaignToShare] = useState<any>(null);

  const { campaigns: allCampaigns, isInitialLoading: allCampaignsLoading } = useCampaigns();
  
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);
  const { deleteLoading, deleteSuccess } = useSelector((state: RootState) => state.campaignSchedule);
  const { deleting: deletingCampaignId } = useSelector((state: RootState) => state.campaigns);
  const { data: integrations = [], loading: integrationsLoading, isFetched: integrationsIsFetched } = useSelector(
    (state: RootState) => state.integrations
  );
  const { data: restaurantData, loading: restaurantLoading, error: restaurantError } = useSelector((state: RootState) => state.restaurant);
  const { combinedEvents, loading: eventsLoading } = useSelector((state: RootState) => state.upcomingEvents);

  const logoImage = restaurantData?.restaurantDetails?.images?.find(img => img.type === 'logo');
  const restaurantName = restaurantData?.name || 'Guest';
  const firstLetter = restaurantName.charAt(0).toUpperCase();

  const marketingCampaigns = allCampaigns;
  const campaignsLoading = allCampaignsLoading;
  
  // Track if events have been fetched to prevent infinite loop when API returns empty array
  const hasAttemptedEventsFetch = React.useRef(false);

  // Handle schedule delete success - close dialog
  useEffect(() => {
    if (deleteSuccess) {
      setShowDeleteDialog(false);
      setCampaignToDelete(null);
    }
  }, [deleteSuccess]);

  const wasDeletingRef = React.useRef(false);
  
  useEffect(() => {
    // Track if we're currently deleting
    if (deletingCampaignId !== null) {
      wasDeletingRef.current = true;
    }
    
    // If we were deleting and now it's null = deletion complete
    if (wasDeletingRef.current && deletingCampaignId === null && deleteType === 'campaign' && campaignToDelete) {
      setShowDeleteDialog(false);
      setCampaignToDelete(null);
      wasDeletingRef.current = false; // Reset the flag
    }
  }, [deletingCampaignId, campaignToDelete, deleteType]);

  useEffect(() => {
    if (!restaurantId) return;
    
    if (!integrationsIsFetched && !integrationsLoading) {
      dispatch(fetchIntegrationsRequest({ restaurantId }));
    }
    
    if (!restaurantData && !restaurantLoading && !restaurantError) {
      dispatch(fetchRestaurantRequest());
    }
    
    // Only fetch events once, even if API returns empty array
    if (!hasAttemptedEventsFetch.current && !eventsLoading) {
      hasAttemptedEventsFetch.current = true;
      const countryCode = 'US';
      dispatch(fetchUpcomingEventsRequest({ restaurantId, countryCode }));
    }
  }, [dispatch, restaurantId, integrationsIsFetched, integrationsLoading, restaurantData, restaurantLoading, restaurantError, eventsLoading]);

  const scheduledCampaigns = React.useMemo(() => {
    return marketingCampaigns
      .filter((campaign: any) => {
        const hasScheduleData = campaign.scheduleData && 
          typeof campaign.scheduleData === 'object' && 
          Object.keys(campaign.scheduleData).length > 0 &&
          campaign.scheduleData.scheduleDate;
        
        const hasPublishData = campaign.publishData && 
          Array.isArray(campaign.publishData) && 
          campaign.publishData.length > 0;
        
        return hasScheduleData && !hasPublishData;
      })
      .map((campaign: any) => ({
        id: campaign.id,
        title: campaign.title || 'Untitled Campaign',
        image: campaign.flyer?.image?.storage_url,
        platforms: campaign.scheduleData?.channels?.map((ch: any) => ch.name || ch.channel || ch) || [],
        startDate: campaign.scheduleData?.scheduleDate,
        type: campaign.type
      }))
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  }, [marketingCampaigns]);

  const allPostedCampaigns = React.useMemo(() => {
    return marketingCampaigns
      .filter((campaign: any) =>
        campaign.publishData &&
        Array.isArray(campaign.publishData) &&
        campaign.publishData.length > 0 &&
        campaign.publishData.some((pub: any) => pub.status === 'posted')
      )
      .map((campaign: any) => {
        const validFrom = campaign.campaignData?.validFrom || campaign.validFrom;
        const validTo = campaign.campaignData?.validTo || campaign.validTo;
        let isCompleted = false;

        if (validFrom && validTo) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const validToDate = new Date(validTo);
          validToDate.setHours(0, 0, 0, 0);
          isCompleted = today.getTime() > validToDate.getTime();
        }
        const platforms = campaign.publishData?.filter((pub: any) => pub.status === 'posted').map((pub: any) => pub.channel) || [];
        return {
          id: campaign.id,
          title: campaign.title || campaign.campaign?.campaignName || 'Untitled Campaign',
          image: campaign.flyer?.image?.storage_url || campaign.campaign?.flyerUrl,
          platforms,
          publishData: campaign.publishData,
          validFrom,
          validTo,
          isCompleted,
          type: campaign.type
        };
      });
  }, [marketingCampaigns]);

  const ongoingCampaigns = React.useMemo(() => {
    return allPostedCampaigns.filter(campaign => !campaign.isCompleted);
  }, [allPostedCampaigns]);

  const completedCampaigns = React.useMemo(() => {
    return allPostedCampaigns.filter(campaign => campaign.isCompleted);
  }, [allPostedCampaigns]);

  const unscheduledCampaigns = React.useMemo(() => {
    return [...(allCampaigns || [])].filter(campaign => {
        const campaignData = campaign as any;
        const hasPublishData = campaignData.publishData && Array.isArray(campaignData.publishData) && campaignData.publishData.length > 0;
      
        const hasScheduleData = campaignData.scheduleData && 
          typeof campaignData.scheduleData === 'object' && 
          Object.keys(campaignData.scheduleData).length > 0 &&
          campaignData.scheduleData.scheduleDate;
        
        return !hasPublishData && !hasScheduleData;
      })
      .sort((a, b) => {
        const createdAtA = new Date(a.createdAt).getTime();
        const createdAtB = new Date(b.createdAt).getTime();
        return createdAtB - createdAtA;
      })
      .map(campaign => ({
        id: campaign.id,
        title: campaign.title || 'Untitled Campaign',
        image: (campaign.flyer?.image as any)?.image?.storage_url || campaign.flyer?.image?.storage_url || '',
        platforms: [],
        createdAt: campaign.createdAt,
        type: campaign.type
      }));
  }, [allCampaigns]);

  const fourBigIdeas = React.useMemo(() => {
    return (allCampaigns || [])
      .filter((campaign: any) => campaign.type === 'system_generated')
      .slice(0, 4)
      .map((campaign: any) => ({
        id: campaign.id,
        title: campaign.title || 'Untitled Campaign',
        image: campaign.flyer?.image?.image?.storage_url || campaign.flyer?.image?.storage_url || '',
        platforms: [],
        type: campaign.type
      }));
  }, [allCampaigns]);

  const handleCampaignClick = (campaignId: string, isOngoing: boolean) => {
    if (!restaurantId) return;
    if (isOngoing) {
      navigate(`/${restaurantId}/analytics?campaignId=${campaignId}`);
    } else {
      const campaign = [...scheduledCampaigns, ...fourBigIdeas, ...unscheduledCampaigns].find(c => c.id === campaignId);
      if (campaign) {
        navigate(`/${restaurantId}/flyer?campaignId=${campaignId}&campaignType=${campaign.type}`);
      }
    }
  };

  const handleDeleteCampaign = (campaignId: string) => {
    setCampaignToDelete(campaignId);
    setDeleteType('campaign');
    setShowDeleteDialog(true);
  };

  const handleDeleteSchedule = (campaignId: string) => {
    // Show confirmation dialog before deleting schedule
    setCampaignToDelete(campaignId);
    setDeleteType('schedule');
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (campaignToDelete) {
      if (deleteType === 'schedule') {
        dispatch(deleteScheduleRequest({ campaignId: campaignToDelete }));
      } else {
        dispatch(deleteCampaignRequest(campaignToDelete));
      }
    }
  };

  const handleShareCampaign = (campaign: any) => {
    setCampaignToShare(campaign);
    setShowShareModal(true);
  };

  const handleRescheduleCampaign = (campaignId: string) => {
    // Find the campaign from all campaigns
    const campaign = marketingCampaigns.find((c: any) => c.id === campaignId);
    if (campaign) {
      // Extract existing schedule data and convert UTC to local
      let initialScheduleData = {};
      const campaignAny = campaign as any;
      if (campaignAny.scheduleData?.scheduleDate) {
        const utcDate = new Date(campaignAny.scheduleData.scheduleDate);
        const localDateStr = utcDate.toISOString().split('T')[0]; // YYYY-MM-DD
        const localTimeStr = utcDate.toTimeString().slice(0, 5); // HH:MM
        const platforms = campaignAny.scheduleData.channels?.map((ch: any) => ch.name) || [];
        
        initialScheduleData = {
          fromDate: localDateStr,
          time: localTimeStr,
          selectedMediums: platforms
        };
      }
      
      setCampaignToShare({
        id: campaign.id,
        title: campaign.title,
        image: campaign.flyer?.image?.storage_url,
        type: campaign.type,
        initialOption: 'schedule-later', // Skip option selection
        initialScheduleData: initialScheduleData // Pre-fill form
      });
      setShowShareModal(true);
    }
  };

  const hasNoData = !campaignsLoading &&
    ongoingCampaigns.length === 0 &&
    scheduledCampaigns.length === 0 &&
    fourBigIdeas.length === 0 &&
    unscheduledCampaigns.length === 0;

  if (hasNoData) {
    return (
      <DashboardEmptyState
        logoImage={logoImage}
        restaurantName={restaurantName}
        firstLetter={firstLetter}
        onCreateCampaign={() => navigate('/campaign-creation')}
      />
    );
  }

  return (
    <div className="">
      <DashboardHeader
        logoImage={logoImage}
        restaurantName={restaurantName}
        firstLetter={firstLetter}
        onCreateCampaign={() => navigate('/campaign-creation')}
      />

      {/* Ongoing Campaigns */}
      <CampaignScrollSection
        icon={FaBullhorn}
        title="Ongoing"
        campaigns={ongoingCampaigns}
        isLoading={campaignsLoading}
        cardType="default"
        containerId="ongoing-scroll"
        onCardClick={handleCampaignClick}
      />

      {/* Scheduled Campaigns */}
      <CampaignScrollSection
        icon={FaCalendarAlt}
        title="Scheduled"
        campaigns={scheduledCampaigns}
        isLoading={campaignsLoading}
        cardType="scheduled"
        containerId="scheduled-scroll"
        onCardClick={handleCampaignClick}
        onDelete={handleDeleteSchedule}
        onReschedule={handleRescheduleCampaign}
      />

      {/* Four Big Ideas */}
      <CampaignScrollSection
        icon={FaLightbulb}
        title="Four Big Ideas"
        campaigns={fourBigIdeas}
        isLoading={allCampaignsLoading}
        cardType="fourBigIdeas"
        containerId="ideas-scroll"
        onCardClick={handleCampaignClick}
      />

      {/* Unscheduled Campaigns */}
      <CampaignScrollSection
        icon={FaBoxOpen}
        title="Unscheduled"
        campaigns={unscheduledCampaigns}
        isLoading={allCampaignsLoading}
        cardType="unscheduled"
        containerId="unscheduled-scroll"
        onCardClick={handleCampaignClick}
        onDelete={handleDeleteCampaign}
        onShare={handleShareCampaign}
      />

      <CampaignScrollSection
        icon={FaCheckCircle}
        title="Completed"
        campaigns={completedCampaigns}
        isLoading={campaignsLoading}
        cardType="default"
        containerId="completed-scroll"
        onCardClick={handleCampaignClick}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setCampaignToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={deleteType === 'schedule' ? 'Delete Schedule?' : 'Delete Campaign?'}
        message={
          deleteType === 'schedule'
            ? 'Are you sure you want to delete this schedule? The campaign will remain but will no longer be scheduled.'
            : 'Are you sure you want to delete this campaign? This action cannot be undone.'
        }
        confirmText={deleteType === 'schedule' ? 'Delete Schedule' : 'Delete Campaign'}
        cancelText="Cancel"
        isDangerous
        isLoading={deleteType === 'schedule' ? deleteLoading : (deletingCampaignId !== null)}
      />

      {/* Share Modal */}
      {campaignToShare && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setCampaignToShare(null);
          }}
          flyerImageUrl={campaignToShare.image}
          generatedContentId={campaignToShare.id}
          flyerId={campaignToShare.id}
          socialMediaData={[]}
          restaurantId={restaurantId || ''}
          campaignId={campaignToShare.id}
          initialOption={campaignToShare.initialOption}
          initialScheduleData={campaignToShare.initialScheduleData}
          onPostNow={() => {
            // Post now handler
          }}
          onSchedule={(data) => {
            // Schedule handler
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
